import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { AppState } from '../../../../state/configureStore';
import { AuthState } from '../../../../state/auth';
import { createTaskInTaskCreate, TaskCreateState } from '../../../../state/taskCreate';
import { ITask } from '../../../../models/task.model';
import CustomCaption from '../../../components/CustomCaption';
import { Grid as MUIGrid } from '@mui/material';
import {
  Close as CloseIcon,
  SkipNext as NextIcon,
  NoteOutlined as NoteAltIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  EventNote as EventNoteIcon,
} from '@material-ui/icons';

const TaskFormDialogSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  description: Yup.string(),
  dueDate: Yup.date().required('Due Date is required').nullable(true),
});

interface TaskFormDialogProps {
  nextClick: () => void;
  closeClick: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      padding: 0,
      background:
        'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 30%), linear-gradient(135deg, #072577 0%, #1d4ed8 55%, #60a5fa 100%)',
    },
    titleContent: {
      padding: theme.spacing(2, 3, 1.5),
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    titleHeading: {
      color: '#f8fafc',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    titleSubtitle: {
      color: 'rgba(226, 232, 240, 0.84)',
      lineHeight: 1.7,
      maxWidth: 520,
    },
    titleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5),
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.14)',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
      color: '#ffffff',
      fontSize: '0.82rem',
      fontWeight: 700,
      backdropFilter: 'blur(8px)',
    },
    closeButton: {
      color: '#ffffff',
      backgroundColor: 'rgba(255,255,255,0.12)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
      },
    },
    content: {
      padding: theme.spacing(2.5, 3, 2),
      background:
        'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%)',
    },
    actions: {
      padding: theme.spacing(0, 3, 2.5),
      justifyContent: 'space-between',
      alignItems: 'center',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,250,252,0.96) 40%)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1),
      },
    },
    actionText: {
      color: '#64748b',
      fontSize: '0.88rem',
      lineHeight: 1.6,
      maxWidth: 420,
    },
    actionShell: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75),
      borderRadius: 999,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.88) 100%)',
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    secondaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
    primaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2.5),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 55%, #60a5fa 100%)',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)',
        boxShadow: '0 16px 34px rgba(37, 99, 235, 0.28)',
      },
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
  }),
);

const TaskFormDialog: React.FC<TaskFormDialogProps> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { nextClick, closeClick } = props;
  const { user, userId } = useSelector<AppState, AuthState>((state) => state.auth);
  const {
    createTaskPayload: {
      subject = '',
      description = '',
      dueDate = null,
      requester = user,
      requesterId = userId,
    },
  } = useSelector<AppState, TaskCreateState>((state) => state.taskCreate);
  const handleSubmit = (values: ITask) => {
    dispatch(createTaskInTaskCreate(values));
    nextClick();
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          subject,
          description,
          dueDate,
          requester,
          requesterId,
        }}
        validationSchema={TaskFormDialogSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
      >
        {({ values, handleChange, errors, setFieldValue }) => {
          return (
            <Form>
              <DialogTitle className={classes.dialogTitle} disableTypography>
                <Box className={classes.titleContent}>
                  <Box>
                    <Typography variant="h4" className={classes.titleHeading}>
                      New Task
                    </Typography>
                    <Typography variant="body1" className={classes.titleSubtitle}>
                      Fill in the details of the task. You can always edit them later if
                      needed.
                    </Typography>
                  </Box>
                  <Grid alignItems="center" justifyContent="flex-end">
                    <IconButton
                      aria-label="close"
                      onClick={() => {
                        closeClick();
                      }}
                      className={classes.closeButton}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.content}>
                <MUIGrid container spacing={1}>
                  <MUIGrid item xs={12}>
                    <CustomCaption
                      caption={'Subject'}
                      icon={<NoteAltIcon fontSize="small" style={{ color: '#4f46e5' }} />}
                    />
                    <TextField
                      name="subject"
                      type="input"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={values.subject}
                      onChange={handleChange}
                      error={!!errors.subject}
                      helperText={errors.subject}
                      placeholder="Enter a clear and concise subject for the task"
                    />
                  </MUIGrid>
                  <MUIGrid item xs={12}>
                    <CustomCaption
                      caption={'Description'}
                      icon={
                        <DescriptionOutlinedIcon
                          fontSize="small"
                          style={{ color: '#0891b2' }}
                        />
                      }
                    />
                    <TextField
                      name="description"
                      type="input"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={values.description}
                      onChange={handleChange}
                      error={!!errors.description}
                      helperText={errors.description}
                      placeholder="Enter more details about the task, such as requirements or instructions for completion"
                      multiline
                      rows={5}
                    />
                  </MUIGrid>
                  {/* <MUIGrid item xs={12} md={6}>
                    <CustomCaption
                      caption={'Requester'}
                      icon={
                        <PersonOutlineIcon
                          fontSize="small"
                          style={{ color: '#f59e0b' }}
                        />
                      }
                    />
                    <TextField
                      name="Requester"
                      type="input"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={user?.fullName || ''}
                      disabled
                    />
                  </MUIGrid> */}
                  <MUIGrid item xs={12}>
                    <CustomCaption
                      caption={'Due Date'}
                      icon={
                        <EventNoteIcon fontSize="small" style={{ color: '#ef4444' }} />
                      }
                    />
                    <KeyboardDatePicker
                      margin="dense"
                      format="yyyy-MM-dd"
                      value={values.dueDate}
                      onChange={(selectedDate, selectedStringDate) => {
                        setFieldValue('dueDate', selectedStringDate);
                      }}
                      showTodayButton={true}
                      helperText={errors.dueDate}
                      error={!!errors.dueDate}
                      inputVariant="outlined"
                      placeholder="Select the due date for the task completion"
                      fullWidth
                    />
                  </MUIGrid>
                </MUIGrid>
              </DialogContent>
              <DialogActions className={classes.actions}>
                <Typography className={classes.actionText}>
                  Keep the task brief clear and deadline realistic before continuing to
                  the assignment step.
                </Typography>
                <Box>
                  <Button
                    onClick={() => {
                      closeClick();
                    }}
                    color="primary"
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    className={classes.secondaryButton}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<NextIcon />}
                    className={classes.primaryButton}
                  >
                    Next
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default TaskFormDialog;
